import { EnumDialogName } from '@calm/evexia-analytics';
import { MediaAssetOrientation } from '@calm/evexia-web-sdk';
import { PlayerVideo } from '@rhg/components/media/types';
import { VideoPlayerDialog } from '@rhg/components/media/video-player-dialog';
import { ControlButtonType } from '@rhg/components/media/video-player/types';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import { useTheme } from '@rhg/libs/material';
import { MediaEvent } from '@rhg/utils/media';
import {
    createContext,
    ReactElement,
    ReactNode,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { VideoPlayerState, OpenVideoPlayerProps } from './types';

export const VideoPlayerContext = createContext<VideoPlayerState>({
    isOpen: false,
    playingVideos: null,
    openVideoPlayer: () => {},
    closeVideoPlayer: () => {},
    autoplayFocusTrap: undefined,
    setAutoplayFocusTrap: () => {},
});

interface Props {
    children: ReactNode;
}
export const VideoPlayerProvider = ({ children }: Props) => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [forcedOrientation, setForcedOrientation] = useState<
        MediaAssetOrientation | undefined
    >(undefined);
    const [playingVideos, setPlayingVideos] = useState<PlayerVideo[] | null>(
        null
    );
    const [isAutoplay, setIsAutoplay] = useState(false);
    const [isCloseButtonDisplayed, setIsCloseButtonDisplayed] = useState(true);
    const [hasMinimalControls, setHasMinimalControls] = useState(false);
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [isInitiallyMuted, setIsInitiallyMuted] = useState(false);
    const [showPoster, setShowPoster] = useState(true);
    const [trackFn, setTrackFn] = useState<MediaTrackerFn | undefined>(
        undefined
    );
    const [trackDialog, setTrackDialog] = useState<EnumDialogName>();
    const [onEnd, setOnEnd] = useState<() => void>();
    const [actionButton, setActionButton] = useState<ReactElement>();
    const [onControlClick, setOnControlClick] =
        useState<(buttonType: ControlButtonType) => void>();

    // Set video and then open last
    const openVideoPlayer = useCallback(
        ({
            videos,
            title,
            subtitle,
            isAutoplay,
            isCloseButtonDisplayed = true,
            showPoster = true,
            isInitiallyMuted = false,
            hasMinimalControls = false,
            trackDialog,
            onEnd,
            actionButton: _actionButton,
            forcedOrientation,
            trackFn,
            onControlClick,
        }: OpenVideoPlayerProps) => {
            setTitle(title ?? '');
            setSubtitle(subtitle ?? '');
            setActionButton(_actionButton);
            setIsCloseButtonDisplayed(!!isCloseButtonDisplayed);
            setHasMinimalControls(!!hasMinimalControls);
            setShowPoster(!!showPoster);
            setIsInitiallyMuted(!!isInitiallyMuted);
            setTrackDialog(trackDialog);
            setForcedOrientation(forcedOrientation);
            setPlayingVideos(videos);
            setOnEnd(() => onEnd);
            setTrackFn(() => trackFn);
            setIsOpen(true);
            setIsAutoplay(!!isAutoplay);
            setOnControlClick(onControlClick);
        },
        []
    );

    // Set video and open first
    // We want this to be on a timeout so that the dialog can transition out before being unmounted
    const closeVideoPlayer = useCallback(() => {
        setIsDialogOpen(false);
        setTimeout(() => {
            setIsOpen(false);
            setIsAutoplay(false);
            setPlayingVideos(null);
            setIsCloseButtonDisplayed(true);
            setHasMinimalControls(false);
            setTitle('');
            setSubtitle('');
            setActionButton(undefined);
            setTrackDialog(undefined);
            setOnEnd(() => undefined);
            setForcedOrientation(undefined);
            setTrackFn(() => undefined);
            setOnControlClick(() => undefined);
        }, theme.transitions.duration.leavingScreen);
    }, [theme.transitions.duration.leavingScreen]);

    const onProgress = useCallback<MediaTrackerFn>(
        (event) => {
            if (event.eventType === MediaEvent.Ended) {
                closeVideoPlayer();
            }
            trackFn?.(event);
        },
        [closeVideoPlayer, trackFn]
    );

    // We want the dialog to render first as close, then open up afterwards so the transition is triggered
    useEffect(() => {
        if (isOpen && playingVideos?.length) {
            setIsDialogOpen(true);
        } else {
            setIsDialogOpen(false);
        }
    }, [
        isOpen,
        playingVideos?.length,
        theme.transitions.duration.enteringScreen,
    ]);

    const [autoplayFocusTrap, setAutoplayFocusTrap] = useState<
        undefined | (() => void)
    >();

    return (
        <>
            <VideoPlayerContext.Provider
                value={{
                    isOpen,
                    playingVideos,
                    openVideoPlayer,
                    closeVideoPlayer,
                    autoplayFocusTrap,
                    setAutoplayFocusTrap,
                }}
            >
                {children}
            </VideoPlayerContext.Provider>
            {isOpen && playingVideos?.length && (
                <VideoPlayerDialog
                    trackDialog={trackDialog}
                    videos={playingVideos}
                    title={title}
                    subtitle={subtitle}
                    isOpen={isDialogOpen}
                    isCloseButtonDisplayed={isCloseButtonDisplayed}
                    hasMinimalControls={hasMinimalControls}
                    isInitiallyMuted={isInitiallyMuted}
                    autoplay={isAutoplay}
                    forcedOrientation={forcedOrientation}
                    onEnd={onEnd}
                    actionButton={actionButton}
                    onClose={closeVideoPlayer}
                    onProgress={onProgress}
                    showPoster={showPoster}
                    onControlClick={onControlClick}
                />
            )}
        </>
    );
};
